/**
 * Universal Format Detection
 *
 * Consolidates all import format detection into a single `detectFormat()` function.
 * Covers CSV, text, and JSON inputs across all import contexts (collection, deck, list).
 *
 * Detection priority:
 * 1. JSON backups (MtgCraft backup signatures)
 * 2. CSV fingerprints (header-based, most specific first)
 * 3. Deck text patterns (zone headers, Arena preamble, MTGO convention)
 * 4. MTGA text (numbered card lines)
 * 5. List text fallback
 */
import { HEADER_ALIASES, SOURCE_FINGERPRINTS, type FormatCategory } from '@/lib/importConstants';
import type { ImportSource } from '@/types/card';
import type { DeckFormat } from '@/types/deck';

export type { FormatCategory } from '@/lib/importConstants';

export interface DetectedFormat {
  /** High-level import context */
  category: FormatCategory;
  /** Specific format identifier (e.g., 'dragonshield-csv', 'mtga-deck-text', 'mtgcraft-list-backup') */
  format: string;
  /** Import source system */
  source: ImportSource;
  /** How confident the detection is */
  confidence: 'exact' | 'heuristic';
  /** What kind of input was detected */
  inputType: 'csv' | 'text' | 'json';
  /** Structural analysis results for pre-filling UI */
  hints?: FormatHints;
}

export interface FormatHints {
  /** Inferred deck format (e.g., 'commander' for 100-card singleton) */
  deckFormat?: DeckFormat;
  /** Total card instances (sum of quantities) */
  cardCount?: number;
  /** Distinct card lines */
  uniqueCards?: number;
  /** All non-basic quantities are 1 */
  isSingleton?: boolean;
  /** Explicit zone definitions found in text */
  hasZoneHeaders?: boolean;
  /** Deck name extracted from comments/preamble */
  suggestedName?: string;
}

// ============================================================================
// Basic land names (for singleton detection)
// ============================================================================

const BASIC_LAND_NAMES = new Set([
  'plains',
  'island',
  'swamp',
  'mountain',
  'forest',
  'wastes',
  'snow-covered plains',
  'snow-covered island',
  'snow-covered swamp',
  'snow-covered mountain',
  'snow-covered forest',
]);

// ============================================================================
// Main Detection Function
// ============================================================================

/**
 * Unified format detection covering all import contexts.
 *
 * @param input - Raw text input (CSV, text card list, or JSON backup)
 * @param preferredCategory - Hint to disambiguate text formats:
 *   'collection' from ImportModal, 'deck' from ImportDeckModal, 'list' from ImportListModal
 * @returns Detected format with metadata, or null if unrecognizable
 */
export function detectFormat(input: string, preferredCategory?: 'collection' | 'deck' | 'list'): DetectedFormat | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // 1. JSON backups (highest priority)
  const jsonResult = detectJsonBackup(trimmed);
  if (jsonResult) return jsonResult;

  // 2. CSV fingerprints
  const csvResult = detectCSVFormat(trimmed);
  if (csvResult) return csvResult;

  // 3. Text formats (deck text, MTGA, list text)
  const textResult = detectTextFormat(trimmed, preferredCategory);
  if (textResult) return textResult;

  return null;
}

// ============================================================================
// JSON Backup Detection
// ============================================================================

function detectJsonBackup(text: string): DetectedFormat | null {
  if (!text.startsWith('{') && !text.startsWith('[')) return null;

  try {
    const parsed = JSON.parse(text);

    // All MtgCraft backups use BackupBase<T> with a `type` discriminator
    switch (parsed.type) {
      case 'mtgcraft_deck':
        if (parsed.deck) {
          return {
            category: 'backup',
            format: 'mtgcraft-deck-backup',
            source: 'manual',
            confidence: 'exact',
            inputType: 'json',
          };
        }
        break;

      case 'mtgcraft_decks':
        if (Array.isArray(parsed.decks)) {
          return {
            category: 'backup',
            format: 'mtgcraft-multi-deck-backup',
            source: 'manual',
            confidence: 'exact',
            inputType: 'json',
          };
        }
        break;

      case 'mtgcraft_list':
        if (parsed.list) {
          return {
            category: 'backup',
            format: 'mtgcraft-list-backup',
            source: 'manual',
            confidence: 'exact',
            inputType: 'json',
          };
        }
        break;

      case 'mtgcraft_lists':
        if (Array.isArray(parsed.lists)) {
          return {
            category: 'backup',
            format: 'mtgcraft-multi-list-backup',
            source: 'manual',
            confidence: 'exact',
            inputType: 'json',
          };
        }
        break;

      case 'mtgcraft_collection':
        if (Array.isArray(parsed.cards)) {
          return {
            category: 'backup',
            format: 'mtgcraft-collection-backup',
            source: 'manual',
            confidence: 'exact',
            inputType: 'json',
          };
        }
        break;
    }
  } catch {
    // Not valid JSON — fall through to CSV/text detection
  }

  return null;
}

// ============================================================================
// CSV Format Detection
// ============================================================================

function detectCSVFormat(text: string): DetectedFormat | null {
  // Extract and parse headers from first line
  const lines = text.split(/\r?\n/);

  // Dragon Shield CSV files start with "sep=," — skip that line for headers
  let headerLine = lines[0] ?? '';
  if (headerLine.toLowerCase().startsWith('sep=')) {
    headerLine = lines[1] ?? '';
  }

  // Parse header values, stripping quotes
  const rawHeaders = headerLine.split(',').map((h) => h.replace(/"/g, '').trim());

  // Check if this looks like a CSV at all (need at least 2 recognizable headers)
  if (rawHeaders.length < 2) return null;

  // Map raw headers to canonical fields
  const canonicalFields = new Set<string>();
  for (const header of rawHeaders) {
    const canonical = HEADER_ALIASES[header.toLowerCase()];
    if (canonical) {
      canonicalFields.add(canonical);
    }
  }

  // Need at least 2 canonical fields to consider this a CSV
  if (canonicalFields.size < 2) return null;

  // Match against source fingerprints (most specific first)
  for (const fingerprint of SOURCE_FINGERPRINTS) {
    const hasRequired = fingerprint.requiredFields.every((f) => canonicalFields.has(f));
    if (!hasRequired) continue;

    const hasExcluded = fingerprint.excludedFields?.some((f) => canonicalFields.has(f));
    if (hasExcluded) continue;

    if (fingerprint.extraCheck && !fingerprint.extraCheck(text)) continue;

    return {
      category: fingerprint.category,
      format: fingerprint.format,
      source: fingerprint.source,
      confidence: 'exact',
      inputType: 'csv',
    };
  }

  // Unknown CSV — has comma structure but no recognized fingerprint
  if (canonicalFields.has('name') || canonicalFields.has('quantity')) {
    return {
      category: 'collection',
      format: 'unknown-csv',
      source: 'unknown',
      confidence: 'heuristic',
      inputType: 'csv',
    };
  }

  return null;
}

// ============================================================================
// Text Format Detection
// ============================================================================

/** Zone header patterns for deck text detection */
const ZONE_HEADER_PATTERNS = [
  /^\/\/\s*(?:commander|companion|sideboard|maybeboard|tokens?|deck|mainboard|outside\s*the\s*game|creatures?|planeswalkers?|artifacts?|enchantments?|instants?|sorceries|sorcery|battles?|lands?|other)\s*(?:\(\d+\))?\s*$/i,
  /^(?:commander|companion|deck|mainboard|sideboard|maybeboard|tokens?)\s*(?:\(\d+\))?\s*:?\s*$/i,
];

function isZoneHeaderLine(line: string): boolean {
  return ZONE_HEADER_PATTERNS.some((p) => p.test(line.trim()));
}

/** MTGA-style card line: "4 Card Name" or "4x Card Name" or "4 Card Name (SET) 123" */
const CARD_LINE_PATTERN = /^(\d+)x?\s+(.+?)(?:\s+\(([A-Za-z0-9]+)\)\s+\S+)?(?:\s+\*[FE]\*)?$/;

function detectTextFormat(text: string, preferredCategory?: 'collection' | 'deck' | 'list'): DetectedFormat | null {
  const lines = text.split(/\r?\n/);
  const nonEmptyLines = lines.filter((l) => l.trim());
  if (nonEmptyLines.length === 0) return null;

  // Check for Arena preamble ("About" + "Name ...")
  const hasArenaPreamble = lines.some((l) => /^About$/i.test(l.trim())) && lines.some((l) => /^Name\s+.+$/i.test(l.trim()));

  // Check for zone headers
  const hasZoneHeaders = lines.some((l) => isZoneHeaderLine(l));

  // Check for format comment (// Format: Commander)
  let commentFormat: string | undefined;
  let suggestedName: string | undefined;
  for (const line of lines) {
    const trimmed = line.trim();
    const formatMatch = trimmed.match(/^(?:\/\/|#)\s*Format:\s*(.+)$/i);
    if (formatMatch) {
      commentFormat = formatMatch[1].trim().toLowerCase();
    }
    const nameMatch = trimmed.match(/^Name\s+(.+)$/i);
    if (nameMatch && !suggestedName) {
      suggestedName = nameMatch[1].trim();
    }
    const deckNameMatch = trimmed.match(/^\/\/\s*Deck:\s*(.+)$/i);
    if (deckNameMatch && !suggestedName) {
      suggestedName = deckNameMatch[1].trim();
    }
  }

  // Count card lines and gather heuristics
  const cardLines = nonEmptyLines.filter((l) => {
    const trimmed = l.trim();
    // Skip comments, zone headers, and preamble
    if (trimmed.startsWith('//') || trimmed.startsWith('#')) return false;
    if (isZoneHeaderLine(trimmed)) return false;
    if (/^About$/i.test(trimmed)) return false;
    if (/^Name\s+.+$/i.test(trimmed)) return false;
    return CARD_LINE_PATTERN.test(trimmed) || /^\S/.test(trimmed);
  });

  if (cardLines.length === 0) return null;

  // Run structural heuristics
  const hints = analyzeTextHeuristics(cardLines, {
    hasZoneHeaders,
    hasArenaPreamble,
    commentFormat,
    suggestedName,
  });

  // Determine category
  const isDeckLike = hasZoneHeaders || hasArenaPreamble || commentFormat != null;
  const hasCardLinePattern = cardLines.some((l) => CARD_LINE_PATTERN.test(l.trim()));

  if (!hasCardLinePattern) {
    // Lines exist but none match card patterns — likely not a card list
    return null;
  }

  let category: FormatCategory;
  let format: string;

  if (isDeckLike) {
    category = 'deck';
    if (hasArenaPreamble) {
      format = 'arena-deck-text';
    } else if (hasZoneHeaders) {
      format = 'moxfield-deck-text';
    } else {
      format = 'deck-text';
    }
  } else if (preferredCategory === 'deck') {
    category = 'deck';
    format = 'mtga-deck-text';
  } else if (preferredCategory === 'list') {
    category = 'list';
    format = 'text-list';
  } else {
    // Default: collection (from ImportModal) or fallback to MTGA-style text
    category = preferredCategory ?? 'collection';
    format = 'mtga-text';
  }

  return {
    category,
    format,
    source: 'unknown',
    confidence: isDeckLike ? 'exact' : 'heuristic',
    inputType: 'text',
    hints,
  };
}

// ============================================================================
// Text Heuristics (lightweight structural analysis)
// ============================================================================

interface HeuristicsContext {
  hasZoneHeaders: boolean;
  hasArenaPreamble: boolean;
  commentFormat?: string;
  suggestedName?: string;
}

function analyzeTextHeuristics(cardLines: string[], ctx: HeuristicsContext): FormatHints {
  let totalInstances = 0;
  let uniqueCards = 0;
  let maxNonBasicQty = 0;

  for (const line of cardLines) {
    const trimmed = line.trim();
    const match = trimmed.match(/^(\d+)x?\s+(.+?)(?:\s+\(([A-Za-z0-9]+)\)\s+\S+)?(?:\s+\*[FE]\*)?$/);

    if (match) {
      const qty = parseInt(match[1], 10);
      const name = match[2].trim();
      totalInstances += qty;
      uniqueCards++;

      if (!BASIC_LAND_NAMES.has(name.toLowerCase())) {
        maxNonBasicQty = Math.max(maxNonBasicQty, qty);
      }
    } else {
      // Line without quantity prefix — assume 1
      totalInstances += 1;
      uniqueCards++;
      if (!BASIC_LAND_NAMES.has(trimmed.toLowerCase())) {
        maxNonBasicQty = Math.max(maxNonBasicQty, 1);
      }
    }
  }

  const isSingleton = maxNonBasicQty === 1 && uniqueCards > 1;

  // Infer deck format
  let deckFormat: DeckFormat | undefined;

  // Explicit format from comment takes priority
  if (ctx.commentFormat) {
    const normalized = ctx.commentFormat.toLowerCase();
    const formatMap: Record<string, DeckFormat> = {
      commander: 'commander',
      standard: 'standard',
      pioneer: 'pioneer',
      modern: 'modern',
      legacy: 'legacy',
      vintage: 'vintage',
      pauper: 'pauper',
      brawl: 'brawl',
      oathbreaker: 'oathbreaker',
      casual: 'casual',
      draft: 'draft',
      sealed: 'sealed',
    };
    deckFormat = formatMap[normalized];
  }

  // Heuristic: 100 cards + all singletons → commander
  if (!deckFormat && totalInstances === 100 && isSingleton) {
    deckFormat = 'commander';
  }

  return {
    deckFormat,
    cardCount: totalInstances > 0 ? totalInstances : undefined,
    uniqueCards: uniqueCards > 0 ? uniqueCards : undefined,
    isSingleton: uniqueCards > 1 ? isSingleton : undefined,
    hasZoneHeaders: ctx.hasZoneHeaders || undefined,
    suggestedName: ctx.suggestedName,
  };
}
